import { PlaylistAddCheckOutlined, SellOutlined } from '@mui/icons-material';
import { Box, LinearProgress, Skeleton, styled } from '@mui/material';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BodyText } from '@/components/BodyText';
import { useGetLabelingCoverage } from '@/hooks/api/assets/useGetLabelingCoverage';
import { useGetMetadataCoverage } from '@/hooks/api/assets/useGetMetadataCoverage';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { ASSETS_WITHOUT_METADATA_VIEW, METADATA_VIEW_PARAM } from '@/pages/MetadataPage/MetadataPage';
import { ASSETS_WITHOUT_LABELS_VIEW, TAXONOMY_VIEW_PARAM } from '@/pages/TaxonomyPage/TaxonomyPage';
import { useTenants } from '@/services/tenants/useTenants';
import { DashboardTile } from './DashboardTile';
import { TileEmptyState } from './TileEmptyState';
import { useUploadAssetsAction } from './useUploadAssetsAction';

const InlineLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const ManageFieldsLink = styled(Link)(({ theme }) => ({
  flexShrink: 0,
  fontSize: '0.875rem',
  textDecoration: 'none',
  color: theme.palette.secondary.main,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const LineHeadingLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  fontSize: '0.875rem',
  fontWeight: 600,
  '&:hover': {
    color: theme.palette.secondary.main,
  },
}));

// A line whose page the reader cannot open still shows its heading, just not as a link into a
// blank page.
const LineHeading = styled(BodyText)({
  fontSize: '0.875rem',
  fontWeight: 600,
});

const CoverageLine = ({
  title,
  to,
  covered,
  total,
  summary,
  percentageTestId,
  children,
}: {
  title: string;
  to?: string;
  covered: number;
  total: number;
  summary: string;
  percentageTestId: string;
  children?: ReactNode;
}) => {
  const coveragePercentage = (covered / total) * 100;

  return (
    <Box display='flex' flexDirection='column' gap='8px'>
      <Box display='flex' alignItems='baseline' justifyContent='space-between' gap='12px'>
        {to ? (
          <LineHeadingLink to={to} title={`Go to ${title.toLowerCase()}`}>
            {title}
          </LineHeadingLink>
        ) : (
          <LineHeading>{title}</LineHeading>
        )}
        <BodyText fontSize='1.5rem' fontWeight={600} data-testid={percentageTestId}>
          {`${Math.floor(coveragePercentage)}%`}
        </BodyText>
      </Box>
      <BodyText color='text.secondary' display='block'>
        {summary}
      </BodyText>
      <LinearProgress variant='determinate' value={coveragePercentage} />
      {children}
    </Box>
  );
};

export const CoverageTile = () => {
  const { getPath } = useTenants();
  const { permissions } = useGetCurrentUser();
  const { isTaxonomyEnabled } = useFeatureFlags();
  const uploadAssetsAction = useUploadAssetsAction();
  const { data: metadataCoverage, isLoading, isError } = useGetMetadataCoverage();
  const { data: labelingCoverage, isError: isLabelingError } = useGetLabelingCoverage({ enabled: isTaxonomyEnabled });

  const hasAssets = !!metadataCoverage && metadataCoverage.totalAssets > 0;
  const assetsWithoutMetadataPath = `${getPath('metadata')}?${METADATA_VIEW_PARAM}=${ASSETS_WITHOUT_METADATA_VIEW}`;
  const assetsWithoutLabelsPath = `${getPath('taxonomy')}?${TAXONOMY_VIEW_PARAM}=${ASSETS_WITHOUT_LABELS_VIEW}`;
  const uncoveredAssets = metadataCoverage ? metadataCoverage.totalAssets - metadataCoverage.coveredAssets : 0;
  const unlabeledAssets = labelingCoverage ? labelingCoverage.totalAssets - labelingCoverage.labeledAssets : 0;

  return (
    <DashboardTile
      id='coverage'
      title='Enrichment'
      icon={<SellOutlined />}
      action={
        permissions.canManageMetadataFields ? (
          <ManageFieldsLink to={getPath('metadata')}>Manage fields</ManageFieldsLink>
        ) : undefined
      }
    >
      {isError && <BodyText color='text.secondary'>Failed to load metadata coverage.</BodyText>}
      {!isError && isLoading && <Skeleton variant='rounded' height={120} />}
      {!isError && !isLoading && !hasAssets && (
        <TileEmptyState
          testId='no-metadata-coverage'
          graphic={<PlaylistAddCheckOutlined />}
          title='Nothing to enrich yet'
          description='Shows how many of your parts carry metadata and labels. Upload parts to start.'
          action={uploadAssetsAction}
        />
      )}
      {!isError && !isLoading && hasAssets && (
        <>
          <CoverageLine
            title='Metadata'
            to={getPath('metadata')}
            covered={metadataCoverage.coveredAssets}
            total={metadataCoverage.totalAssets}
            summary={`${metadataCoverage.coveredAssets.toLocaleString()} of ${metadataCoverage.totalAssets.toLocaleString()} assets have metadata`}
            percentageTestId='metadata-coverage-percentage'
          >
            {uncoveredAssets > 0 && (
              <BodyText color='text.secondary'>
                <InlineLink to={assetsWithoutMetadataPath} data-testid='view-assets-without-metadata'>
                  {`View the ${uncoveredAssets.toLocaleString()} assets without metadata`}
                </InlineLink>
              </BodyText>
            )}
          </CoverageLine>
          {isTaxonomyEnabled && isLabelingError && (
            <BodyText color='text.secondary'>Failed to load labeling coverage.</BodyText>
          )}
          {isTaxonomyEnabled && !isLabelingError && !!labelingCoverage?.totalAssets && (
            <CoverageLine
              title='Labels'
              to={permissions.canManageTaxonomy ? getPath('taxonomy') : undefined}
              covered={labelingCoverage.labeledAssets}
              total={labelingCoverage.totalAssets}
              summary={`${labelingCoverage.labeledAssets.toLocaleString()} of ${labelingCoverage.totalAssets.toLocaleString()} assets labeled`}
              percentageTestId='labeling-coverage-percentage'
            >
              {/* Only an admin can open the taxonomy page, so for everyone else the count stays
                  and the link that would land them on a blank page goes. */}
              {unlabeledAssets > 0 && permissions.canManageTaxonomy && (
                <BodyText color='text.secondary'>
                  <InlineLink to={assetsWithoutLabelsPath} data-testid='view-assets-without-labels'>
                    {`View the ${unlabeledAssets.toLocaleString()} assets without labels`}
                  </InlineLink>
                </BodyText>
              )}
            </CoverageLine>
          )}
        </>
      )}
    </DashboardTile>
  );
};
